import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { API_BASE_URL, authFetch } from '../config/api';

const VAULT_ROOT = '2 Areas/Food Safety';

// ── Template registry (frontend-only) ─────────────────────────────────
//
// Each template pre-seeds the frontmatter and body skeleton for a common
// note type. No server-side registry — the backend just takes whatever
// we send and writes it to disk. Keep these in sync with CLAUDE.md's
// "knowledge notes style" note: Wikipedia-style factual entries.

export interface Template {
  id: 'hazard' | 'substance' | 'regulation' | 'qms';
  label: string;
  icon: string;
  folder: string; // relative to VAULT_ROOT
  blurb: string;
  frontmatter: Record<string, unknown>;
  body: string; // {title} is replaced on render
}

const today = () => new Date().toISOString().slice(0, 10);

const TEMPLATES: Template[] = [
  {
    id: 'hazard',
    label: 'Hazard',
    icon: '\u26A0\uFE0F',
    folder: `${VAULT_ROOT}/Hazards`,
    blurb: 'Biological, chemical or physical hazard. Fills severity + product-relevance up front.',
    frontmatter: {
      tags: ['Food Safety', 'Hazards'],
      domain: 'Food Safety > Hazards',
      severity: '',
      'product-relevance': '',
      'last-updated': today(),
      source: '',
    },
    body: [
      '# {title}',
      '',
      '## TL;DR',
      '',
      '',
      '## Key Characteristics',
      '',
      '| Property | Value |',
      '|----------|-------|',
      '|          |       |',
      '',
      '## Disease',
      '',
      '',
      '## Sources',
      '',
      '',
    ].join('\n'),
  },
  {
    id: 'substance',
    label: 'Substance',
    icon: '\u{1F9EA}',
    folder: `${VAULT_ROOT}/Substances`,
    blurb: 'Pesticide, mycotoxin, residue or other substance. Slot for CAS + MRL refs.',
    frontmatter: {
      tags: ['Food Safety', 'Substances'],
      domain: 'Food Safety > Substances',
      'cas-number': '',
      'mrl-refs': '',
      'last-updated': today(),
      source: '',
    },
    body: [
      '# {title}',
      '',
      '## TL;DR',
      '',
      '',
      '## Occurrence',
      '',
      '',
      '## Regulatory limits',
      '',
      '',
      '## Sources',
      '',
      '',
    ].join('\n'),
  },
  {
    id: 'regulation',
    label: 'Regulation',
    icon: '\u{1F4DC}',
    folder: `${VAULT_ROOT}/Regulations`,
    blurb: 'EU / FSA / FDA regulation or directive. Jurisdiction + in-force-date up front.',
    frontmatter: {
      tags: ['Food Safety', 'Regulations'],
      domain: 'Food Safety > Regulations',
      jurisdiction: '',
      'in-force-date': '',
      'last-updated': today(),
      source: '',
    },
    body: [
      '# {title}',
      '',
      '## Summary',
      '',
      '',
      '## Scope',
      '',
      '',
      '## Key requirements',
      '',
      '',
      '## Sources',
      '',
      '',
    ].join('\n'),
  },
  {
    id: 'qms',
    label: 'QMS procedure',
    icon: '\u{1F4CB}',
    folder: `${VAULT_ROOT}/QMS`,
    blurb: 'Internal procedure tied to one or more BRC clauses. Purpose + responsibility + records.',
    frontmatter: {
      tags: ['Food Safety', 'QMS'],
      domain: 'Food Safety > QMS',
      'applies-to': '',
      'brc-clauses': '',
      'last-updated': today(),
      owner: '',
    },
    body: [
      '# {title}',
      '',
      '## Purpose',
      '',
      '',
      '## Responsibility',
      '',
      '',
      '## Procedure',
      '',
      '',
      '## Records',
      '',
      '',
    ].join('\n'),
  },
];

// ── Modal ───────────────────────────────────────────────────────────

interface Props {
  onClose: () => void;
  /** Called after the note is created; receives its vault-relative path. */
  onCreated: (path: string) => void;
}

interface CreateResponse {
  ok: boolean;
  path: string;
  title: string;
  template: string | null;
  error?: string;
}

async function createNote(payload: {
  folder: string;
  title: string;
  template: string;
  frontmatter: Record<string, unknown>;
  body: string;
}): Promise<CreateResponse> {
  const res = await authFetch(`${API_BASE_URL}/api/knowledge/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = (await res.json()) as CreateResponse;
  if (!res.ok) {
    throw new Error(data.error || `HTTP ${res.status}`);
  }
  return data;
}

export default function NewNoteModal({ onClose, onCreated }: Props) {
  const [selected, setSelected] = useState<Template | null>(null);
  const [title, setTitle] = useState('');

  const mut = useMutation(createNote, {
    onSuccess: (data) => {
      onCreated(data.path);
    },
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected || !title.trim()) return;
    const body = selected.body.replace(/\{title\}/g, title.trim());
    mut.mutate({
      folder: selected.folder,
      title: title.trim(),
      template: selected.id,
      frontmatter: selected.frontmatter,
      body,
    });
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal new-note-modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal__header">
          <div>
            <h3 className="modal__title">
              {selected ? `New ${selected.label.toLowerCase()}` : 'New note'}
            </h3>
            <div className="modal__subtitle">
              {selected ? (
                <>
                  <span className="text-muted">Destination:</span>{' '}
                  <code>{selected.folder}</code>
                </>
              ) : (
                <span className="text-muted">Pick a template to pre-fill frontmatter and sections.</span>
              )}
            </div>
          </div>
          <button
            className="modal__close"
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            {'\u2715'}
          </button>
        </header>

        <div className="modal__body">
          {!selected && (
            <div className="new-note-modal__templates">
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className="new-note-modal__template-card"
                  onClick={() => setSelected(t)}
                >
                  <span className="new-note-modal__template-icon" aria-hidden>
                    {t.icon}
                  </span>
                  <span className="new-note-modal__template-label">{t.label}</span>
                  <span className="new-note-modal__template-blurb">{t.blurb}</span>
                </button>
              ))}
            </div>
          )}

          {selected && (
            <form className="new-note-modal__form" onSubmit={handleCreate}>
              <label className="new-note-modal__field">
                <span className="new-note-modal__label">Title</span>
                <input
                  type="text"
                  className="new-note-modal__input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={placeholderForTemplate(selected.id)}
                  autoFocus
                  disabled={mut.isLoading}
                />
                <span className="new-note-modal__hint text-muted">
                  Will be saved as <code>{selected.folder}/{title.trim() || '…'}.md</code>
                </span>
              </label>

              <details className="new-note-modal__preview">
                <summary>Preview seeded frontmatter</summary>
                <pre className="new-note-modal__preview-code">
                  {previewFrontmatter(selected.frontmatter)}
                </pre>
              </details>

              {mut.error && (
                <div className="new-note-modal__error">
                  Error: {(mut.error as Error).message}
                </div>
              )}
            </form>
          )}
        </div>

        <footer className="modal__footer">
          {selected ? (
            <>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => {
                  setSelected(null);
                  setTitle('');
                  mut.reset();
                }}
                disabled={mut.isLoading}
              >
                ← Back
              </button>
              <button
                type="button"
                className="btn btn--approve"
                onClick={handleCreate}
                disabled={mut.isLoading || !title.trim()}
              >
                {mut.isLoading ? 'Creating…' : 'Create note'}
              </button>
            </>
          ) : (
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Cancel
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────

function placeholderForTemplate(id: Template['id']): string {
  switch (id) {
    case 'hazard':
      return 'e.g. Listeria monocytogenes';
    case 'substance':
      return 'e.g. Glyphosate';
    case 'regulation':
      return 'e.g. EU 2023_915 - Contaminants';
    case 'qms':
      return 'e.g. Internal Audit Procedure';
  }
}

function previewFrontmatter(fm: Record<string, unknown>): string {
  const lines: string[] = ['---'];
  for (const [k, v] of Object.entries(fm)) {
    if (Array.isArray(v)) {
      lines.push(`${k}: ${JSON.stringify(v)}`);
    } else if (v === '' || v === null || v === undefined) {
      lines.push(`${k}:`);
    } else {
      lines.push(`${k}: ${String(v)}`);
    }
  }
  lines.push('---');
  return lines.join('\n');
}
